import React, {
  useState,
  useContext,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useLocation, useHistory } from 'react-router-dom';
import queryString from 'querystring';

import Logo from '@prisjakt/ui-logo';
import { AppContext } from '@prisjakt/ui-app-context';
import { withViewport } from '@prisjakt/ui-viewport';
import { Hideable } from '@prisjakt/ui/hideable';

import SearchBar from '../../components/search-bar';
import Viewport from '../../components/viewport';
import headerActions from './actions';
import searchActions from '../search/actions/search';
import menuActions from '../navigation/actions/desktop';
import mobileMenuActions from '../navigation/actions/mobile-menu';
import NavigationMenu from '../navigation/index';
import MobileMenuButton from '../navigation/components/mobile-menu-button';
import NoScroll from '../../components/no-scroll';
import Suggestions from './components/suggestions';
import {
  useProductCollectionSuggestionsExperiment,
  useOfferCollectionSuggestionsExperiment,
  useIncludePriceOnProductSuggestions,
} from '../../hooks/experiments';

import { AppState } from '../../reducers';

import * as S from './styles';

type OwnProps = {
  viewport: {
    top: number;
  };
};

type StateProps = {
  mobileMenuOpen?: boolean;
  megaMenuOpen?: boolean;
  showSearchBar: boolean;
};

type DispatchProps = {
  searchFocus: () => void;
  searchBlur: () => void;
  closeMobileMenu: () => void;
  toggleMenu: (state: { open: boolean }) => void;
  setShowSearchBar: (state: boolean) => void;
};

type Props = OwnProps & StateProps & DispatchProps;

const Header = ({
  mobileMenuOpen,
  megaMenuOpen = false,
  toggleMenu,
  closeMobileMenu,
  searchFocus,
  searchBlur,
  viewport,
  setShowSearchBar,
  showSearchBar,
}: Props) => {
  const location = useLocation();
  const history = useHistory();

  const megaMenuButtonRef = useRef<HTMLButtonElement>(null);

  const { t, market } = useContext(AppContext);

  const [query, setQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  const setQueryAndUpdateSuggestions = useCallback((q: string) => {
    setQuery(q);
    setShowSuggestions(q.length > 0);
  }, []);

  // Update search query on URL change
  useEffect(() => {
    const qs = queryString.parse(location.search.substr(1));
    const queryParam = qs.search || qs.q || '';
    setQuery(Array.isArray(queryParam) ? queryParam[0] : queryParam);
  }, [location.search]);

  const toggleMegaMenu = useCallback(
    e => {
      // Prevent event from triggering an "outside click" for the menu
      e.stopPropagation();

      toggleMenu({ open: !megaMenuOpen });
    },
    [toggleMenu, megaMenuOpen]
  );

  const toggleSearchBar = useCallback(() => {
    setShowSearchBar(!showSearchBar);

    if (mobileMenuOpen) {
      closeMobileMenu();
    }
  }, [mobileMenuOpen, closeMobileMenu, setShowSearchBar, showSearchBar]);

  const clearQueryAndHideSuggestions = useCallback(() => {
    setQuery('');
    setShowSuggestions(false);
  }, []);

  const closeSearchBar = useCallback(() => {
    setShowSearchBar(false);
    clearQueryAndHideSuggestions();
  }, [clearQueryAndHideSuggestions, setShowSearchBar]);

  const onCloseMegaMenu = useCallback(() => {
    if (megaMenuButtonRef.current) {
      megaMenuButtonRef.current.focus();
    }
  }, []);

  const {
    showProductCollectionSuggestions,
  } = useProductCollectionSuggestionsExperiment();

  const {
    showOfferCollectionSuggestions,
  } = useOfferCollectionSuggestionsExperiment();

  const includePriceOnProductSuggestions = useIncludePriceOnProductSuggestions();

  const searchFocused = /search=/.test(location.search);
  const isFrontpage = location.pathname === '/';
  const isSearchPage = location.pathname.indexOf('/search') === 0;

  const isSidebarOpen = mobileMenuOpen;
  const isScrolledDown = viewport && viewport.top > 90;
  const isMobileSearchOpen =
    (showSearchBar || isSearchPage || (isFrontpage && !isScrolledDown)) &&
    !isSidebarOpen;
  const menuButtonTitle = megaMenuOpen
    ? t('symbols', 'symbol.close')
    : t('header', 'categories.title');

  useEffect(() => {
    // Hide the search bar when changing route on mobile
    if (history.action === 'PUSH' && !searchFocused && !isSearchPage) {
      closeSearchBar();
    }
  }, [location, history, searchFocused, isSearchPage, closeSearchBar]);
  return (
    <>
      <S.FixedMargin mobileSearchOpen={isMobileSearchOpen} />
      <S.HeaderBar mobileSearchOpen={isMobileSearchOpen}>
        <S.Wrapper>
          <S.MenuColumn data-test="MobileMenuColumn">
            <S.MenuItem>
              <MobileMenuButton />
            </S.MenuItem>
          </S.MenuColumn>

          <S.LogoColumn>
            <S.MegaMenuItem title={menuButtonTitle} placement="right">
              <S.MegaMenuButton
                type="button"
                onClick={toggleMegaMenu}
                aria-label={menuButtonTitle}
                active={megaMenuOpen}
                aria-expanded={megaMenuOpen}
                aria-haspopup
                ref={megaMenuButtonRef}
                data-test="MegaMenuToggleButton"
              >
                {megaMenuOpen ? (
                  <S.CloseMegaMenuIcon size={30} />
                ) : (
                  <S.MegaMenuIcon size={30} />
                )}
              </S.MegaMenuButton>
            </S.MegaMenuItem>

            <S.LogoLink
              to="/"
              onClick={() => mobileMenuOpen && closeMobileMenu()}
            >
              <Logo
                market={market.code}
                companyName={t('meta', 'market.logo-alt')}
                styling="v2-light"
              />
            </S.LogoLink>
          </S.LogoColumn>

          <S.SearchColumn tablet desktop>
            <SearchBar
              query={query}
              setQuery={setQueryAndUpdateSuggestions}
              clearQuery={clearQueryAndHideSuggestions}
              autoFocus={isFrontpage}
              onFocusChange={searchFocus}
              onBlurChange={searchBlur}
              focused
            />
          </S.SearchColumn>

          <S.RightColumn>
            {Boolean(market.showPromoText) && (
              <S.PromoText>{t('header', 'logo.promo-text')}</S.PromoText>
            )}
            <Viewport mobile>
              <Hideable visible={!isMobileSearchOpen}>
                <S.MenuItem>
                  <button
                    style={{ height: 50 }}
                    type="button"
                    onClick={toggleSearchBar}
                    aria-label={t('header', 'search.button')}
                  >
                    <S.SearchIcon size={30} />
                  </button>
                </S.MenuItem>
              </Hideable>
            </Viewport>

            <Viewport tablet>
              <S.MenuItem wideOnTablet>
                <MobileMenuButton handleOnClick={closeSearchBar} />
              </S.MenuItem>
            </Viewport>
          </S.RightColumn>
        </S.Wrapper>
        <S.SearchBox visible={isMobileSearchOpen}>
          <S.SearchColumn mobile>
            <SearchBar
              query={query}
              setQuery={setQueryAndUpdateSuggestions}
              clearQuery={clearQueryAndHideSuggestions}
              onFocusChange={searchFocus}
              onBlurChange={searchBlur}
              autoFocusOnExpand={showSearchBar && !isFrontpage}
            />
          </S.SearchColumn>
        </S.SearchBox>

        {showSuggestions && (
          <Suggestions
            query={query}
            setQuery={setQuery}
            onClose={() => setShowSuggestions(false)}
            showProductCollectionSuggestions={showProductCollectionSuggestions}
            showOfferCollectionSuggestions={showOfferCollectionSuggestions}
            includePriceOnProductSuggestions={includePriceOnProductSuggestions}
          />
        )}
        <NoScroll mobile enabled={showSuggestions} />
      </S.HeaderBar>

      <NavigationMenu onCloseMegaMenu={onCloseMegaMenu} />
    </>
  );
};

const mapStateToProps = (state: AppState) => ({
  mobileMenuOpen: state.areas.navigation.mobileMenu.open,
  megaMenuOpen: state.areas.navigation.megaMenu.open,
  showSearchBar: state.areas.header.showSearchBar,
});

const dispatchActions = {
  searchFocus: searchActions.searchFocus,
  searchBlur: searchActions.searchBlur,
  closeMobileMenu: mobileMenuActions.closeMenu,
  toggleMenu: menuActions.toggleMenu,
  setShowSearchBar: headerActions.setShowSearchBar,
};

export default compose(
  withViewport,
  connect(mapStateToProps, dispatchActions)
)(Header);
